import {
  Link as FluentLink,
  Body1Strong,
  makeStyles,
  tokens,
  Dropdown,
  Option,
  SelectionEvents,
  OptionOnSelectData,
  Text,
  Button,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  OptionGroup,
} from '@fluentui/react-components';
import { SettingsRegular, TagRegular } from '@fluentui/react-icons';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';

import {
  TOverflowButtonParam,
  DateLabel,
  ControlledTooltip,
  IconLabel,
  ChangeNoteRow,
  NavOverviewLayout,
  VersionLinkRow,
} from '@shared/components';
import { DRAFT_VERSION_VALUE } from '@shared/components/VersionDropdown';
import { useSearchParams } from '@shared/hooks';

import { UserLabel, SignInContext } from '@modules/users';
import {
  DocumentContext,
  useScrollContent,
  SelectVersionDialog,
  isTDocument,
  TDocument,
  TDraft,
  TVersion,
} from '@modules/documents';
import { TFile, TFileVersion } from '@modules/files';
import { TReplica, useUpdate as useUpdateReplica } from '@modules/replicas';
import { ApprovalStatus, ApprovalStatusButton, TViewApprovalDialogState, type TApproval } from '@modules/approvals';

import {
  isTDraft,
  isDocumentItemVersion,
  isFileItemVersion,
  isInlineBlockItemVersion,
  TItemVersion,
} from '../../types/items.types';
import { TPermissions } from '@modules/access/types';
import { useCanEdit } from '@modules/access/hooks/useCanEdit.hook';

import { useGetVersionInfo } from '../../api/items.api';
import { TSelectVersionDialogState } from '../../types/dialogs.types';
import TemplateInfo from './TemplateInfo';
import VariantInfo from './VariantInfo';
import useItemScreenStore, { EItemScreenMode } from '@modules/items/stores/useItemScreenStore';
import { ToastContext } from '@shared/contexts';
import { IMPACTS_NOTIFICATIONS } from '@modules/impacts';
import { ESubmissionStatus, TSubmissionItemInfo } from '@modules/submissions';
import { TUpdateSubmissionItemDialogState } from '@modules/submissions';
import { UpdateSubmissionItemDialog } from '@modules/submissions';
import { ECustomFieldType } from '../../../users/types/organizations.types';
import { TERMINOLOGY } from '@shared/i18n';
import { isTInlineBlock, TInlineBlock, TInlineBlockVersion } from '@modules/inlineBlocks';

import { buildHref, getBlockTagLabel, filterTagsForDisplay, findSelectedTaggedBlock } from '@shared/utils';
import resolveDefaultComparisonBase, {
  resolvePreviousVersionId,
} from '../../helpers/resolveDefaultComparisonBase.helper';
import compareVersion from '../../helpers/compareVersion.helper';
import {
  ConfirmTrainingDialog,
  isTrainingTriggerDocument,
  useResolvedTraining,
  useTrainingStore,
} from '@modules/mocks/training';
import { formatDateOnly } from '@shared/utils/date';
import { isPeriodicReviewDue } from '../../helpers/periodicReviewDates.helper';

const useStyles = makeStyles({
  comparisonListbox: {
    minWidth: '160px',
    maxHeight: '200px',
  },
  dropdown: {
    minWidth: 0,
    width: '95px',
  },
  potentialImpactsButton: {
    width: 'fit-content',
    padding: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'start',
    gap: '.1rem',
  },
  syncedVersionButton: {
    paddingLeft: 0,
    paddingRight: 0,
    minWidth: 0,
  },
  trainingButton: {
    color: tokens.colorStatusWarningForeground1,
    border: `1px solid ${tokens.colorStatusWarningBorder1}`,
    '&:hover': {
      color: tokens.colorStatusWarningForeground1,
      border: `1px solid ${tokens.colorStatusWarningBorderActive}`,
      backgroundColor: tokens.colorStatusWarningBackground1,
    },
    '&:hover:active': {
      color: tokens.colorStatusWarningForeground1,
      border: `1px solid ${tokens.colorStatusWarningBorderActive}`,
      backgroundColor: tokens.colorStatusWarningBackground2,
    },
  },
  noOverflow: {
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'block',
  },
});

type TProps = {
  item: TDocument | TFile | TInlineBlock;
  replica?: TReplica;
  actions: TOverflowButtonParam[];
  itemVersion?: TItemVersion;
  permissions: TPermissions;
  setViewApprovalDialogState?: (state?: TViewApprovalDialogState) => void;
  openPeriodicReviewDialog?: () => void;
  extraContent?: ReactNode;
};

const DRAFT_VERSION = {
  VALUE: DRAFT_VERSION_VALUE,
  LABEL: 'Draft',
};

const NavOverview = ({
  item,
  replica,
  actions,
  itemVersion,
  permissions,
  setViewApprovalDialogState,
  openPeriodicReviewDialog,
  extraContent,
}: TProps) => {
  const { organization } = useContext(SignInContext);
  const { canEdit } = useCanEdit(permissions.accessRight);
  const documentContext = useContext(DocumentContext);
  const { show } = useContext(ToastContext);
  const { change, searchParams } = useSearchParams();
  const view = searchParams.get('view');
  const { screenMode, setScreenMode, comparisonBaseVersionId, setComparisonBaseVersionId } = useItemScreenStore();
  const training = useResolvedTraining();
  const trainingCompletion = useTrainingStore((state) => state.completion);
  const isTrainingActive = useTrainingStore((state) =>
    training ? state.activeDocumentIds.includes(training.documentId) : false
  );
  const [isConfirmTrainingOpen, setIsConfirmTrainingOpen] = useState(false);

  useEffect(() => {
    if (!isTrainingActive) setIsConfirmTrainingOpen(false);
  }, [isTrainingActive]);

  const handleOptionSelect = (_event: SelectionEvents, data: OptionOnSelectData) => {
    /**
     * Post change impact analysis is not available for drafts
     * and should be closed when switching to draft version.
     */
    if (
      data.selectedOptions[0] === DRAFT_VERSION.VALUE &&
      screenMode &&
      [EItemScreenMode.POST_CHANGE_IMPACT_ANALYSIS, EItemScreenMode.IMPACT_ANALYSIS_SUMMARY].includes(screenMode)
    ) {
      setScreenMode();
      show('info', IMPACTS_NOTIFICATIONS.POST_CHANGE_NOT_POSSIBLE_FOR_DRAFT);
    }

    // Change only if different from current. This avoids unnecessary re-renders of DocumentContext provider.
    if (data.selectedOptions[0] !== view) change('view', data.selectedOptions[0]);
    // FUTURE: setSelectedTab('home');
  };

  const version = itemVersion?.version;
  const isDoc = isTDocument(item);
  const isInlineBlock = isTInlineBlock(item);
  const periodicReview = isInlineBlock ? undefined : item.periodicReview;

  const isBlock = isDoc && item.isBlock;
  const isDraft = isTDraft(version);
  const isDocOrFileVersion = isDocumentItemVersion(itemVersion) || isFileItemVersion(itemVersion);
  const rootPathItem = item.resolvedPath[0];
  const isQm = rootPathItem?._id === organization.folders.qm;
  const isCountries = rootPathItem?._id === organization.folders.countries;
  const replicaIsCountries = replica?.resolvedPath[0]?._id === organization.folders.countries;
  const isTrainingDocument = Boolean(training && isTrainingTriggerDocument(organization.mock?.training, item._id));

  const selectedTaggedBlocks = (version as TDraft | TVersion)?.selectedTaggedBlocks ?? [];
  const taggedBlockTags = useMemo(() => {
    const selectedTagIds = new Set(selectedTaggedBlocks.map((stb) => stb.blockTagId));
    return filterTagsForDisplay(organization.blockTags ?? [], selectedTagIds);
  }, [organization.blockTags, selectedTaggedBlocks]);

  const approvals = !isInlineBlock ? item.approvals : [];
  // history is appended chronologically, so the last entry is the most recent review.
  const lastPeriodicReview = periodicReview?.history?.at(-1);
  const versionId = isDraft ? DRAFT_VERSION.VALUE : version?.id;
  const approvedVersionId = item.stats.approvedVersionId;
  const approval = approvals
    .slice()
    .reverse()
    .find((approval) => approval.versionId === versionId);
  const approvedApproval =
    !isDraft && !isInlineBlock && versionId === approvedVersionId
      ? approvals
          .slice()
          .reverse()
          .find(
            (approval) =>
              approval.versionId === versionId &&
              approval.status === ApprovalStatus.APPROVED &&
              Boolean(approval.dateCompleted)
          )
      : undefined;

  const latestVersion: TVersion | TFileVersion | TInlineBlockVersion | undefined = item.versions[0];

  const [showErrors, setShowErrors] = useState(false);

  const scrollContent = useScrollContent();

  const [selectVersionDialogState, setSelectVersionDialogState] = useState<TSelectVersionDialogState | undefined>(
    undefined
  );

  const getVersionInfo = useGetVersionInfo();
  const updateReplica = useUpdateReplica();
  const openReplicaSettings = async () => {
    if (!replica) return;
    const versionInfo = await getVersionInfo.mutateAsync({ id: replica._id, itemType: replica.type });

    setSelectVersionDialogState({
      title: `Select version to ${TERMINOLOGY.synchronize.lower}`,
      isOpen: true,
      versionInfo,
      versionMode: replica.versionMode,
      versionId: replica.versionId,
      onSelect: async (versionMode, versionId) => {
        await updateReplica.mutateAsync({ _id: replica._id, versionMode, versionId });
        setSelectVersionDialogState(undefined);
      },
      buttonLabel: 'Update',
    });
  };

  const togglePostChangeImpactAnalysisResults = () => {
    if (screenMode === EItemScreenMode.IMPACT_ANALYSIS_SUMMARY) setScreenMode();
    else setScreenMode(EItemScreenMode.IMPACT_ANALYSIS_SUMMARY);
  };

  // submission integration:

  const openSubmissionInfos = !isInlineBlock
    ? item.submissionItemInfos.filter((s) => s.status === ESubmissionStatus.IN_PREPARATION)
    : [];
  const [updateSubmissionItemDialogState, setUpdateSubmissionItemDialogState] = useState<
    TUpdateSubmissionItemDialogState | undefined
  >(undefined);

  const getHandleOpenSubmissionClick = (openSubmissionInfo: TSubmissionItemInfo) => () => {
    if (openSubmissionInfos.length > 0 && !isInlineBlock)
      setUpdateSubmissionItemDialogState({
        isOpen: true,
        item,
        submissionInfo: openSubmissionInfo,
        submissionItem: openSubmissionInfo.submissionItem,
      });
  };

  useEffect(() => {
    const submissionId = searchParams.get('submission');
    if (submissionId && !isInlineBlock) {
      const submissionInfo = item.submissionItemInfos.find((s) => s._id === submissionId);
      if (submissionInfo) {
        setUpdateSubmissionItemDialogState({
          isOpen: true,
          item,
          submissionInfo,
          submissionItem: submissionInfo.submissionItem,
        });
      }
    }
  }, []);

  const classes = useStyles();

  // --- Comparison base for Changes mode ---
  const isChangesMode = screenMode === EItemScreenMode.CHANGES;
  const docVersions = isDoc || isInlineBlock ? (item.versions as TVersion[]) : [];

  const previousVersionId = useMemo(
    () => (versionId ? resolvePreviousVersionId(docVersions, versionId) : undefined),
    [versionId, docVersions]
  );

  const olderVersions = useMemo(() => {
    if (!versionId) return [];
    if (versionId === DRAFT_VERSION.VALUE) return docVersions;
    const index = docVersions.findIndex((v) => v.id === versionId);
    if (index === -1) return [];
    return docVersions.slice(index + 1);
  }, [versionId, docVersions]);

  const showApprovedOption =
    !!approvedVersionId &&
    !!versionId &&
    compareVersion.isLessThan(approvedVersionId, versionId) &&
    docVersions.some((v) => v.id === approvedVersionId);

  useEffect(() => {
    if (!isChangesMode || !versionId) return;
    const defaultBase = resolveDefaultComparisonBase(
      docVersions,
      versionId,
      approvals as TApproval[],
      approvedVersionId
    );
    setComparisonBaseVersionId(defaultBase);
  }, [isChangesMode, versionId]);

  const comparisonBaseLabel = comparisonBaseVersionId ?? '-';
  const comparisonBaseSelectedOptions = comparisonBaseVersionId ? [comparisonBaseVersionId] : [];

  const handleComparisonBaseSelect = (_event: SelectionEvents, data: OptionOnSelectData) => {
    const selected = data.selectedOptions[0];
    if (!selected) return;

    // Resolve pinned shortcut values
    if (selected === '__previous__') setComparisonBaseVersionId(previousVersionId);
    else if (selected === '__approved__') setComparisonBaseVersionId(approvedVersionId);
    else setComparisonBaseVersionId(selected);
  };

  const itemRows = (
    <>
      {isTInlineBlock(item) && item.blockTagId && (
        <p className="flex items-center">
          <span className="block w-32 flex-shrink-0">Tag:</span>
          <Body1Strong>
            <IconLabel icon={<TagRegular />}>{getBlockTagLabel(organization.blockTags, item.blockTagId)}</IconLabel>
          </Body1Strong>
        </p>
      )}

      {isDoc && (
        <TemplateInfo item={item} version={version as TDraft | TVersion | undefined} permissions={permissions} />
      )}

      {item.resolvedPath.length > 0 && !isCountries && (
        <p className="flex">
          <span className="block w-32 flex-shrink-0">{isQm ? 'Workspace' : 'Product'}:</span>
          <Link to={buildHref(`/${isQm ? 'folder' : 'product'}/${item.resolvedPath[0]._id}`)}>
            <FluentLink>
              <Body1Strong className="force-break-word">{item.resolvedPath[0].name}</Body1Strong>
            </FluentLink>
          </Link>
        </p>
      )}

      {isCountries && item.resolvedPath.length > 1 && (
        <p className="flex">
          <span className="block w-32 flex-shrink-0">Country:</span>
          <Link to={buildHref(`/folder/${item.resolvedPath[1]._id}`)}>
            <FluentLink>
              <Body1Strong className="force-break-word">{item.resolvedPath[1].name}</Body1Strong>
            </FluentLink>
          </Link>
        </p>
      )}

      {isCountries && item.resolvedPath.length > 2 && (
        <p className="flex">
          <span className="block w-32 flex-shrink-0">Registration:</span>
          <Link to={buildHref(`/folder/${item.resolvedPath[2]._id}`)}>
            <FluentLink>
              <Body1Strong className="force-break-word">{item.resolvedPath[2].name}</Body1Strong>
            </FluentLink>
          </Link>
        </p>
      )}

      {!replica &&
        !isQm &&
        !isCountries &&
        item.resolvedPath.length > 1 &&
        !(item as TDocument).isBlock &&
        !isInlineBlock && (
          <p className="flex">
            <span className="block w-32 flex-shrink-0">Jurisdiction:</span>
            <Link to={buildHref(`/folder/${item.resolvedPath[1]._id}`)}>
              <FluentLink>
                <Body1Strong className="force-break-word">{item.resolvedPath[1].name}</Body1Strong>
              </FluentLink>
            </Link>
          </p>
        )}

      {replica && !replicaIsCountries && replica.resolvedPath.length > 1 && (
        <p className="flex">
          <span className="block w-32 flex-shrink-0">Jurisdiction:</span>
          <Link to={buildHref(`/folder/${replica.resolvedPath[1]._id}`)}>
            <FluentLink>
              <Body1Strong className="force-break-word">{replica.resolvedPath[1].name}</Body1Strong>
            </FluentLink>
          </Link>
        </p>
      )}

      {replica && replicaIsCountries && replica.resolvedPath.length > 1 && (
        <p className="flex">
          <span className="block w-32 flex-shrink-0">Country:</span>
          <Link to={buildHref(`/folder/${replica.resolvedPath[1]._id}`)}>
            <FluentLink>
              <Body1Strong className="force-break-word">{replica.resolvedPath[1].name}</Body1Strong>
            </FluentLink>
          </Link>
        </p>
      )}

      {replica && replicaIsCountries && replica.resolvedPath.length > 2 && (
        <p className="flex">
          <span className="block w-32 flex-shrink-0">Registration:</span>
          <Link to={buildHref(`/folder/${replica.resolvedPath[2]._id}`)}>
            <FluentLink>
              <Body1Strong className="force-break-word">{replica.resolvedPath[2].name}</Body1Strong>
            </FluentLink>
          </Link>
        </p>
      )}

      {openSubmissionInfos.length > 0 && (
        <div className={classNames('flex items-center', { '!mt-3.5 !-mb-1.5': Boolean(approval) })}>
          <p className="block w-32 flex-shrink-0">Submissions:</p>
          {openSubmissionInfos.length === 1 ? (
            <Button onClick={getHandleOpenSubmissionClick(openSubmissionInfos[0])}>
              {openSubmissionInfos.length} open
            </Button>
          ) : (
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <Button>{openSubmissionInfos.length} open</Button>
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  {openSubmissionInfos.map((submissionInfo) => (
                    <MenuItem key={submissionInfo._id} onClick={getHandleOpenSubmissionClick(submissionInfo)}>
                      {submissionInfo.title}
                    </MenuItem>
                  ))}
                </MenuList>
              </MenuPopover>
            </Menu>
          )}
        </div>
      )}

      {replica && (
        <p className="flex items-center">
          <span className="block w-32 flex-shrink-0">{`${TERMINOLOGY.synchronized.upper} version:`}</span>
          {(() => {
            const versionLabel =
              replica.versionMode === 'AUTO'
                ? 'Auto'
                : replica.versionId === DRAFT_VERSION.VALUE
                  ? DRAFT_VERSION.LABEL
                  : replica.versionId;

            return canEdit ? (
              <Button
                size="small"
                appearance="transparent"
                icon={<SettingsRegular />}
                iconPosition="after"
                onClick={openReplicaSettings}
                className={classes.syncedVersionButton}
              >
                <Body1Strong>{versionLabel}</Body1Strong>
              </Button>
            ) : (
              <Body1Strong>{versionLabel}</Body1Strong>
            );
          })()}
        </p>
      )}

      <VersionLinkRow
        label="Latest version"
        versionId={latestVersion?.id}
        isCurrent={version === latestVersion}
        getParams={(id) => ({ view: id })}
      />
      {!isInlineBlock && (
        <VersionLinkRow
          label="Approved version"
          versionId={approvedVersionId}
          isCurrent={versionId === approvedVersionId}
          getParams={(id) => ({ view: id })}
        />
      )}
      {!replica &&
        !item.isFrozen &&
        periodicReview?.enabled &&
        periodicReview.nextReviewDate &&
        openPeriodicReviewDialog && (
          <p className="flex">
            <span className="block w-32 flex-shrink-0">Next review:</span>
            <FluentLink
              className={isPeriodicReviewDue(periodicReview.nextReviewDate) ? '!text-red-500' : undefined}
              onClick={openPeriodicReviewDialog}
            >
              <Body1Strong>{formatDateOnly(periodicReview.nextReviewDate)}</Body1Strong>
            </FluentLink>
          </p>
        )}
      {!replica && item.isFrozen && lastPeriodicReview?.reviewDate && openPeriodicReviewDialog && (
        <p className="flex">
          <span className="block w-32 flex-shrink-0">Last review:</span>
          <FluentLink onClick={openPeriodicReviewDialog}>
            <Body1Strong>{formatDateOnly(lastPeriodicReview.reviewDate)}</Body1Strong>
          </FluentLink>
        </p>
      )}
    </>
  );

  return (
    <>
      <SelectVersionDialog state={selectVersionDialogState} setState={setSelectVersionDialogState} />
      <UpdateSubmissionItemDialog
        state={updateSubmissionItemDialogState}
        setState={setUpdateSubmissionItemDialogState}
        isItemView
      />
      <ConfirmTrainingDialog
        isOpen={isTrainingDocument && isTrainingActive && isConfirmTrainingOpen}
        setIsOpen={setIsConfirmTrainingOpen}
        documentId={item._id}
        documentName={item.name}
        versionId={versionId ?? ''}
      />

      <NavOverviewLayout
        actions={actions}
        versionDropdownProps={{
          versions: item.versions,
          hasDraft: (isDoc || isInlineBlock) && !!item.draft,
          selectedVersion: versionId,
          onOptionSelect: handleOptionSelect,
          oldestVersionDisabledReason: isChangesMode ? 'No previous version to compare against.' : undefined,
        }}
        itemRows={itemRows}
      >
        {isChangesMode && olderVersions.length > 0 && (
          <div className="flex !-mb-1.5 items-center">
            <span className="block w-32 flex-shrink-0">Comparing against:</span>
            <Dropdown
              selectedOptions={comparisonBaseSelectedOptions}
              value={comparisonBaseLabel}
              onOptionSelect={handleComparisonBaseSelect}
              appearance="outline"
              listbox={{ className: classes.comparisonListbox }}
              className={classes.dropdown}
            >
              <OptionGroup label="Quick select">
                {previousVersionId && (
                  <Option value="__previous__" text={`Previous (${previousVersionId})`}>
                    Previous ({previousVersionId})
                  </Option>
                )}
                {showApprovedOption && (
                  <Option value="__approved__" text={`Approved (${approvedVersionId})`}>
                    Approved ({approvedVersionId})
                  </Option>
                )}
              </OptionGroup>
              <OptionGroup label="All versions">
                {olderVersions.map((v) => (
                  <Option key={v.id} value={v.id}>
                    {v.id}
                  </Option>
                ))}
              </OptionGroup>
            </Dropdown>
          </div>
        )}

        {version && (
          <>
            {approvedApproval?.dateCompleted ? (
              <p className="flex">
                <span className="block w-32 flex-shrink-0">Approved:</span>
                <Body1Strong>
                  <DateLabel timeZone="UTC">{approvedApproval.dateCompleted}</DateLabel>
                </Body1Strong>
              </p>
            ) : isFileItemVersion(itemVersion) && !isTDraft(itemVersion.version) ? (
              <p className="flex">
                <span className="block w-32 flex-shrink-0">Uploaded:</span>
                <Body1Strong>
                  <DateLabel>{itemVersion.version.date}</DateLabel>
                </Body1Strong>
              </p>
            ) : (isDocumentItemVersion(itemVersion) || isInlineBlockItemVersion(itemVersion)) &&
              !isTDraft(itemVersion.version) ? (
              <p className="flex">
                <span className="block w-32 flex-shrink-0">Completed:</span>
                <Body1Strong>
                  <DateLabel>{itemVersion.version.completedDate}</DateLabel>
                </Body1Strong>
              </p>
            ) : (
              <p className="flex">
                <span className="block w-32 flex-shrink-0">Modified:</span>
                <Body1Strong>
                  <DateLabel>{item.stats.modifiedDate}</DateLabel>
                </Body1Strong>
              </p>
            )}

            {!isDraft &&
              !isInlineBlock && ( // secondary
                <div className={classNames('flex items-center', { '!mt-3.5 !-mb-1.5': Boolean(approval) })}>
                  <p className="block w-32 flex-shrink-0">Approval:</p>
                  {approval ? (
                    <ApprovalStatusButton approval={approval} setViewApprovalDialogState={setViewApprovalDialogState} />
                  ) : (
                    <Body1Strong> - </Body1Strong>
                  )}
                </div>
              )}

            {!isDraft && !isInlineBlock && isTrainingDocument && isTrainingActive && (
              <div className="flex items-center">
                <p className="block w-32 flex-shrink-0">Training:</p>
                {trainingCompletion ? (
                  <Body1Strong>Completed</Body1Strong>
                ) : (
                  <Button className={classes.trainingButton} onClick={() => setIsConfirmTrainingOpen(true)}>
                    Confirm
                  </Button>
                )}
              </div>
            )}

            <p className="flex">
              <span className="block w-32 flex-shrink-0">Authors:</span>
              <Body1Strong>
                <span className="flex flex-col space-y-3 force-break-word">
                  {(isTDraft(version) ? item.stats.authors : version.authors).map((userId) => (
                    <UserLabel id={userId} key={userId} />
                  ))}
                </span>
              </Body1Strong>
            </p>

            {false && (
              <p className="flex">
                <span className="block w-32 flex-shrink-0">Approvers:</span>
                <Body1Strong>
                  <span className="flex flex-col space-y-3 force-break-word">
                    <span>Lukas Poterpin</span>
                    <span>Jan Andre Schweiger Gottinger</span>
                    <span>Julian Schwarz</span>
                    <span>Hannah Wahlberger</span>
                  </span>
                </Body1Strong>
              </p>
            )}

            {isDocOrFileVersion && !isTDraft(itemVersion.version) && itemVersion.version.impactAnalysis && (
              <p className="flex items-center">
                <span className="block w-32 flex-shrink-0">Impact analysis:</span>
                <Body1Strong>
                  <span className="flex flex-col space-y-3 force-break-word">
                    <FluentLink onClick={togglePostChangeImpactAnalysisResults}>
                      {screenMode === EItemScreenMode.IMPACT_ANALYSIS_SUMMARY ? 'Hide' : 'Show'}
                    </FluentLink>
                  </span>
                </Body1Strong>
              </p>
            )}

            {isDoc &&
              item.invalidRefIds.length > 0 &&
              ((item.draft ? isDraft : latestVersion === version) ? (
                <div>
                  <p className="flex">
                    <span className="block w-32 flex-shrink-0 text-red-500">
                      <Text weight={showErrors ? 'bold' : undefined}>
                        {item.invalidRefIds.length} Error
                        {item.invalidRefIds.length === 1 ? '' : 's'}:
                      </Text>
                    </span>
                    <FluentLink onClick={() => setShowErrors(!showErrors)}>{showErrors ? 'Hide' : 'Show'}</FluentLink>
                  </p>
                  {showErrors && (
                    <ul className="mt-1.5 ml-2.5 flex flex-col space-y-1">
                      {item.invalidRefIds.map((refId, idx) => (
                        <li key={refId} className="list-disc list-inside">
                          <FluentLink onClick={() => scrollContent({ value: refId })}>
                            Invalid {TERMINOLOGY.reference.lower} #{idx + 1}
                          </FluentLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : (
                <p>
                  {item.invalidRefIds.length} Error{item.invalidRefIds.length === 1 ? '' : 's'} in{' '}
                  {isDraft ? DRAFT_VERSION.VALUE : 'latest'}
                </p>
              ))}

            {!isDraft && <ChangeNoteRow note={version.note} />}

            {isDocOrFileVersion &&
              organization.customFields.map((cf) => {
                const customFieldValue = itemVersion.version.customFieldValues.find((v) => v.customField === cf.id);
                if (!customFieldValue) return null;
                let value: string = (customFieldValue as any).value[cf.type];
                if (!value) return null;

                if (cf.type === ECustomFieldType.DROPDOWN)
                  value = cf.options?.find((option) => option.id === value)?.label ?? '-';
                if (cf.type === ECustomFieldType.TEXT) value = value ?? '-';

                return (
                  <div key={cf.id}>
                    <p className="flex">
                      <span className="block w-32 flex-shrink-0">{cf.label}:</span>
                      <Body1Strong className={classes.noOverflow}>
                        {cf.type === ECustomFieldType.DATE ? (
                          value ? (
                            <DateLabel>{value}</DateLabel>
                          ) : (
                            '-'
                          )
                        ) : (
                          <ControlledTooltip enable={value.length > 15} content={value} relationship="label" withArrow>
                            <span>{value}</span>
                          </ControlledTooltip>
                        )}
                      </Body1Strong>
                    </p>
                  </div>
                );
              })}

            {isDoc &&
              version &&
              taggedBlockTags.map((tag) => {
                const sel = findSelectedTaggedBlock(selectedTaggedBlocks, tag.id);
                const blockData = sel ? documentContext.doc.blocksData[sel.blockId] : undefined;
                const blockName = blockData?.itemName;
                // Display the variant-aware resolved value; fall back to block name.
                const displayText = (versionId && blockData?.versions[versionId]?.value) || blockName;
                // Tooltip shows block name + frozen version (if any) - the only place version info is shown.
                // The picker (TaggedSnippets) intentionally hides versions since they're an implementation detail.
                const tooltipText = sel?.blockVersionId ? `${blockName} (v${sel.blockVersionId})` : (blockName ?? '');

                return (
                  <div key={tag.id}>
                    <p className="flex">
                      <span className="block w-32 flex-shrink-0">{tag.label}:</span>
                      {sel && blockName ? (
                        <ControlledTooltip enable content={tooltipText} relationship="label" withArrow>
                          <Body1Strong className="block truncate min-w-0">{displayText}</Body1Strong>
                        </ControlledTooltip>
                      ) : (
                        <Body1Strong>-</Body1Strong>
                      )}
                    </p>
                  </div>
                );
              })}

            {extraContent}
          </>
        )}

        {/* TODO: add `|| isInlineblock` when variants are supported for inline blocks */}
        {isBlock && <VariantInfo />}
      </NavOverviewLayout>
    </>
  );
};

export default NavOverview;
